import React, {FC, useState, useCallback, useEffect} from 'react'
import axios from 'axios'
import {Form, Formik, FormikProps} from 'formik'
import {
  IVehicleSaleModel,
  initialVehicleSale,
  validationVehicleSale,
  CURRENCY_OPTIONS,
  SALES_CHANNEL_OPTIONS,
  AUCTION_COMPANY_OPTIONS,
  SALES_POINT_OPTIONS,
  SALES_CAMPAIGN_OPTIONS,
  TRANSFER_PROCESS_OPTIONS,
  STATUS_OPTIONS,
} from './VehicleSaleModel'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput' 
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import {showSuccessToast, showErrorToast} from '../../../../shared/components/ToastUtils'
import {VehicleSaleTable} from './VehicleSaleTable' // Güncellenmiş tablo bileşeni
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import {AsyncCarModel, AsyncCustomerModel} from '../../../../shared/models/ApiModels'
import {fetchAsyncCarData, fetchAsyncCustomerData} from '../../../../shared/models/SharedApiLookups'

// IVehicleDefinitionDTO tanımı doğrudan buraya eklendi
interface IVehicleDefinitionDTO {
  id: string
  brand: string
  model: string
  vehicleType: string
  fuelType: string
  color: string
  transmission: string
  initialKm: number
}

// IOption tip tanımı eklendi
interface IOption {
  value: string | number
  label: string
}

// Mock API Fonksiyonları (Gerçek API'ye bağlandığınızda bu kısmı değiştirin)
const mockApi = {
  create: async (data: IVehicleSaleModel) => {
    console.log('API Create:', data)
    return {data: {payload: {...data, id: `id-${Math.random()}`}}}
  },
  update: async (data: IVehicleSaleModel) => {
    console.log('API Update:', data)
    return {data: {payload: data}}
  },
  delete: async (id: string) => {
    console.log('API Delete:', id)
    return {data: {payload: {}}}
  },
  load: async (page: number, size: number, sort: string, order: string, filters?: any) => {
    console.log('API Load - Filters:', filters)
    const mockData = Array.from({length: 10}, (_, i) => ({
      ...initialVehicleSale,
      id: `id-${page * size + i}`,
      saleDate: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
      brand: `Marka ${page * size + i}`,
      model: `Model ${page * size + i}`,
      salesPrice: 50000 + i * 1000,
      saleKm: 15000 + i * 100,
      status: 'Satıldı',
      branch: {id: `branch${i}`, branchName: `Şube ${i}`},
      plate: `42 ABC ${page * 10 + i}`,
      transferProcess: 'Satış',
      vehicleType: 'Sedan',
      fuelType: 'Benzin',
    }))
    return {
      data: {
        payload: {
          content: mockData,
          totalPages: 3,
        },
      },
    }
  },
}

const loadVehicleDefinitions = async (query: string): Promise<IOption[]> => {
  const mockData: IVehicleDefinitionDTO[] = [
    {
      id: '1',
      brand: 'Ford',
      model: 'Focus',
      vehicleType: 'Hatchback',
      fuelType: 'Dizel',
      color: 'Beyaz',
      transmission: 'Otomatik',
      initialKm: 15000,
    },
    {
      id: '2',
      brand: 'Renault',
      model: 'Megane',
      vehicleType: 'Sedan',
      fuelType: 'Benzin',
      color: 'Gri',
      transmission: 'Manuel',
      initialKm: 25000,
    },
  ]
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(
        mockData
          .filter((item) =>
            `${item.brand} ${item.model}`.toLowerCase().includes(query.toLowerCase())
          )
          .map((item) => ({
            value: item.id,
            label: `${item.brand} ${item.model} (${item.vehicleType})`,
          }))
      )
    }, 500)
  )
}

const searchCariAccounts = async (query: string): Promise<IOption[]> => {
  const mockData = [
    {value: 'cari1', label: 'Ahmet Yılmaz'},
    {value: 'cari2', label: 'Ayşe Kaya'},
    {value: 'cari3', label: 'Mehmet Demir'},
  ]
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(mockData.filter((item) => item.label.toLowerCase().includes(query.toLowerCase())))
    }, 500)
  )
}

const VehicleSalePage: FC = () => {
  // State tanımlamaları
  const [loading, setLoading] = useState(false)
  const [vehicleSales, setVehicleSales] = useState<IVehicleSaleModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<string | 'form'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('saleDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showModal, setShowModal] = useState(false)
  const [currentFilters, setCurrentFilters] = useState<any>({})

  const tabOptions: TabOption<'form' | 'list'>[] = [
    {key: 'form', title: 'Yeni Araç Satış', icon: 'bi bi-car-front'},
    {key: 'list', title: 'Satış Listesi', icon: 'bi bi-list'},
  ]

  // API'den verileri çekecek ana fonksiyon
  const fetchVehicleSales = useCallback(
    async (
      page: number = 0,
      sort: string = sortBy,
      order: 'asc' | 'desc' = sortOrder,
      filters: any = currentFilters
    ) => {
      setLoading(true)
      try {
        const response = await mockApi.load(page, 10, sort, order, filters)
        setVehicleSales(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        showErrorToast('Satışlar yüklenirken bir hata oluştu.')
      } finally {
        setLoading(false)
      }
    },
    [sortBy, sortOrder, currentFilters]
  )

  // Sayfa yüklendiğinde ve sayfa, sıralama ya da filtreler değiştiğinde veriyi çek
  useEffect(() => {
    if (tab === 'list') {
      fetchVehicleSales(currentPage)
    }
  }, [tab, currentPage, sortBy, sortOrder, fetchVehicleSales])

  const handleSave = async (values: IVehicleSaleModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await mockApi.update({...values, id: selectedId})
        showSuccessToast('Araç satışı başarıyla güncellendi.')
      } else {
        await mockApi.create(values)
        showSuccessToast('Araç satışı başarıyla oluşturuldu.')
      }
      await fetchVehicleSales(currentPage)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu.')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback(
    (sale: IVehicleSaleModel, formik: FormikProps<IVehicleSaleModel>) => {
      formik.setValues(sale)
      setSelectedId(sale.id)
      setTab('form')
    },
    []
  )

  const handleDeleteClick = useCallback((sale: IVehicleSaleModel) => {
    setSelectedId(sale.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await mockApi.delete(selectedId)
        showSuccessToast('Araç satışı başarıyla silindi.')
        await fetchVehicleSales(currentPage)
      } catch (err) {
        showErrorToast('Silme işlemi sırasında bir hata oluştu.')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
      }
    },
    [totalPages]
  )

  const handleSort = useCallback((column: string, order: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(order)
    setCurrentPage(0) // Sıralama değişince ilk sayfaya dön
  }, [])

  const handleVehicleSelect = (
    formik: FormikProps<IVehicleSaleModel>,
    selectedOption: IOption | null
  ) => {
    if (selectedOption) {
      formik.setFieldValue('carDefinitionId', selectedOption.value)
      const mockVehicleDetails = {
        '1': {
          brand: 'Ford',
          model: 'Focus',
          vehicleType: 'Hatchback',
          fuelType: 'Dizel',
          color: 'Beyaz',
          transmission: 'Otomatik',
          initialKm: 15000,
        },
        '2': {
          brand: 'Renault',
          model: 'Megane',
          vehicleType: 'Sedan',
          fuelType: 'Benzin',
          color: 'Gri',
          transmission: 'Manuel',
          initialKm: 25000,
        },
      }
      const details = mockVehicleDetails[selectedOption.value as keyof typeof mockVehicleDetails]
      if (details) {
        formik.setFieldValue('brand', details.brand)
        formik.setFieldValue('model', details.model)
        formik.setFieldValue('fuelType', details.fuelType)
        formik.setFieldValue('vehicleType', details.vehicleType)
        formik.setFieldValue('color', details.color)
        formik.setFieldValue('transmission', details.transmission)
        formik.setFieldValue('initialKm', details.initialKm)
      }
    } else {
      formik.setFieldValue('carDefinitionId', '')
      formik.setFieldValue('brand', '')
      formik.setFieldValue('model', '')
      formik.setFieldValue('fuelType', '')
      formik.setFieldValue('vehicleType', '')
      formik.setFieldValue('color', '')
      formik.setFieldValue('transmission', '')
      formik.setFieldValue('initialKm', null)
    }
  }

  const handleFilter = (filters: any) => {
    setCurrentFilters(filters)
    setCurrentPage(0) // Filtreleme yapıldığında ilk sayfaya dön
  }

  return (
    <>
      <Formik
        initialValues={initialVehicleSale}
        validationSchema={validationVehicleSale}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }
          return (
            <>
              <FormTabHeader
                formId='vehicleSaleForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                tabs={tabOptions}
                setSelectedId={setSelectedId}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'>
                {tab === 'form' && (
                  <Form id='vehicleSaleForm' className='form card-body'>
                    {/* Satış İşlemleri */}
                    <div className='card-title d-flex align-items-center mb-5'>
                      <i className='bi bi-cart fs-2 me-2'></i>
                      <h3 className='fw-bolder m-0'>Araç Satış İşlemleri</h3>
                    </div>
                    <div className='row mb-6 align-items-end'>
                      <TextInput
                        name='saleDate'
                        label='Satış Tarihi'
                        type='date'
                        wrapperClassName='col-md-6'
                      />

                      <TextInput
                        name='targetPrice'
                        label='Hedef Fiyat'
                        type='number'
                        wrapperClassName='col-md-3'
                      />

                      <SelectInput
                        name='currency'
                        label=' '
                        options={CURRENCY_OPTIONS}
                        wrapperClassName='col-md-3'
                      />
                    </div>

                    <div className='row mb-6 align-items-center'>
                      <div className='col-md-6 d-flex align-items-center'>
                        <CheckboxInput
                          name='notForRent'
                          label='Kiraya Verilemez'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <TextInput
                        name='vatRate'
                        label='Satış KDV Oranı'
                        type='number'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <hr className='my-8' />
                    {/* Araç Bilgileri */}
                    <div className='card-title d-flex align-items-center mb-5'>
                      <i className='bi bi-car-front fs-2 me-2'></i>
                      <h3 className='fw-bolder m-0'>Araç Bilgileri</h3>
                    </div>
                    <div className='row mb-6'>
                      <AsyncSearchInput<AsyncCarModel>
                        name="carId"
                        label='Araç Ara'
                        placeholder='Araç bilgisi girin...'
                        fetchHandler={fetchAsyncCarData}
                        displayField={(item) => `${item.plate} (${item.modelYear})`}
                        onSelect={(item) => console.log('Seçilen araç:', item)}
                        onClear={() => console.log('Araç temizlendi')}
                      />
                      <TextInput
                        name='fuelType'
                        label='Yakıt Türü'
                        wrapperClassName='col-md-6'
                        readOnly
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput name='brand' label='Marka' wrapperClassName='col-md-6' readOnly />
                      <TextInput
                        name='transmission'
                        label='Vites'
                        wrapperClassName='col-md-6'
                        readOnly
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='vehicleType'
                        label='Tip'
                        wrapperClassName='col-md-6'
                        readOnly
                      />
                      <TextInput name='color' label='Renk' wrapperClassName='col-md-6' readOnly />
                    </div>
                    <div className='row mb-6'>
                      <TextInput name='model' label='Model' wrapperClassName='col-md-6' readOnly />
                      <TextInput
                        name='initialKm'
                        label='İlK KM'
                        type='number'
                        wrapperClassName='col-md-6'
                        readOnly
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='saleKm'
                        label='Satış KM'
                        type='number'
                        wrapperClassName='offset-md-6 col-md-6'
                      />
                    </div>
                    <hr className='my-8' />
                    {/* Satış Aşaması */}
                    <div className='card-title d-flex align-items-center mb-5'>
                      <i className='bi bi-people-fill fs-2 me-2'></i>
                      <h3 className='fw-bolder m-0'>Satış Aşaması</h3>
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='salesChannel'
                        label='Satış Kanalı'
                        options={SALES_CHANNEL_OPTIONS}
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='auctionCompany'
                        label='İhale Firması'
                        options={AUCTION_COMPANY_OPTIONS}
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='salesPoint'
                        label='Satış Noktası'
                        options={SALES_POINT_OPTIONS}
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='auctionDate'
                        label='İhale Tarihi'
                        type='date'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='salesCampaign'
                        label='Satış Kampanyası'
                        options={SALES_CAMPAIGN_OPTIONS}
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='auctionNumber'
                        label='İhale Sayısı'
                        type='number'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <hr className='my-8' />
                    {/* Satış Yapılan Müşteri */}
                    <div className='card-title d-flex align-items-center mb-5'>
                      <i className='bi bi-person-circle fs-2 me-2'></i>
                      <h3 className='fw-bolder m-0'>Satış Yapılan Müşteri</h3>
                    </div>
                    <div className='row mb-6'>
                      <AsyncSearchInput<AsyncCustomerModel>
                        name="customerId"
                        label='Cari Ara'
                        placeholder='Ad Soyad girin...'
                        fetchHandler={fetchAsyncCustomerData}
                        displayField={(item) => `${item.firstName} ${item.lastName}`}
                        onSelect={(item) => console.log('Seçilen cari:', item)}
                        onClear={() => console.log('Cari temizlendi')}
                      />
                      <TextInput name='cariInfo' label='Cari Bilgi' wrapperClassName='col-md-6' />
                    </div>
                    <hr className='my-8' />
                    {/* Açıklamalar */}
                    <div className='card-title d-flex align-items-center mb-5'>
                      <i className='bi bi-chat-dots fs-2 me-2'></i>
                      <h3 className='fw-bolder m-0'>Açıklamalar</h3>
                    </div>
                    <div className='row mb-6'>
                      <TextAreaInput
                        name='description1'
                        label='1. Açıklama'
                        wrapperClassName='col-md-6'
                      />
                      <TextAreaInput
                        name='description2'
                        label='2. Açıklama'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <hr className='my-8' />
                    {/* Satış Bilgileri */}
                    <div className='card-title d-flex align-items-center mb-5'>
                      <i className='bi bi-gear fs-2 me-2'></i>
                      <h3 className='fw-bolder m-0'>Satış Bilgileri</h3>
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='status'
                        label='Durum'
                        options={STATUS_OPTIONS}
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='salesPrice'
                        label='Satış Fiyatı'
                        type='number'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='transferProcess'
                        label='Devir İşlemi'
                        options={TRANSFER_PROCESS_OPTIONS}
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='notaryInfo'
                        label='Noter Bilgisi'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='journalInfo'
                        label='Yevmiye Bilgisi'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                  </Form>
                )}
                {tab === 'list' && (
                  <div className='card-body pt-0'>
                    <VehicleSaleTable
                      data={vehicleSales}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(sale) => handleEdit(sale, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      onFilter={handleFilter}
                      loading={loading}
                    />
                  </div>
                )}
              </div>
            </>
          )
        }}
      </Formik>
      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu araç satışını silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {VehicleSalePage}
